import React, { useEffect, useState, useContext } from "react";
import uuid from "uuid";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { RouteComponentProps } from "react-router";

import routes from "../constants/routes";
import { Typography, Card } from "@material-ui/core";
import {
  LegalEntity,
  Premises,
  IContactEventType,
  ContactEvent
} from "../constants/interfaces";
import { UIMessageContext } from "../components/UIMessage";

import {
  GENERAL_FILE_HINT_25_FILE_LIMIT,
  MAX_FILE_SIZE,
  GENERAL_FILE_TYPES
} from "../constants/files";
import { connect } from "react-redux";
import { contactEventActions } from "../reducers/contactEvent";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../constants/messages";
import { premisesActions } from "../reducers/premises";
import { RxForm, FormValues, FieldValueOnChange } from "../components/Form";
import SelectField from "../components/Fields/SelectField";
import FormTextField from "../components/Fields/FormTextField";
import FileUploadField from "../components/Fields/FileUploadField";
import { SubmitButton } from "../components/Form/SubmitButton";
import { ResetButton } from "../components/Form/ResetButton";
import { OnUnSaveChange } from "../components/Form/OnUnSaveChange";
import { PageHeader, PageRoot, Suggestion, Button } from "../components";
import api from "../utils/api";

const useStyles = makeStyles(theme => ({
  buttonWrapper: {
    display: "flex",
    marginTop: theme.spacing(2),
    "&>button": {
      marginRight: "1.2em"
    }
  },
  card: {
    overflow: "visible",
    padding: theme.spacing(4)
  },
  titleField: {
    flexGrow: 1,
    marginBottom: theme.spacing(4),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2)
  },
  descriptionField: {
    flexGrow: 1,
    marginBottom: theme.spacing(4),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2)
  },
  dropzone: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1)
  },
  dropzoneWrapper: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  root: {
    display: "flex",
    justifyContent: "center"
  },
  error: {
    marginBottom: theme.spacing(2)
  },
  select: {
    minWidth: "300px",
    margin: theme.spacing(2)
  },
  selectWrapper: {
    display: "flex",
    flexDirection: "column",
    flexFlow: "row wrap",
    marginBottom: theme.spacing(4)
  },
  submitButton: {
    marginRight: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(4)
  },
  row: {
    display: "flex"
  },
  "@media (max-width: 820px)": {
    row: {
      flexDirection: "column"
    }
  }
}));

type Props = RouteComponentProps & {
  classes?: any;
  contactEventTypes: IContactEventType[];
  premises: Premises[];
  submitEvent: (
    event: ContactEvent,
    submitSuccessCallBack?: () => void
  ) => Promise<void>;
  getPremises: () => Promise<void>;
  removeFile: (tempCaseId: string, filename: string) => Promise<void>;
  uploadFile: (tempCaseId: string, file: any) => Promise<void>;
};

const RaiseContactEvent: React.FC<Props> = props => {
  const classes = useStyles();
  const [tempCaseId, resetTempCaseId] = useState(uuid());
  const { showMessage } = useContext(UIMessageContext);
  useEffect(() => {
    props.getPremises();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
        label={routes.CONTACT.label}
      />

      <Card className={classes.card}>

        <RxForm
          confirmMessage={INFORMATION_ACCURACY_CONFIRMATION}
          onSubmit={async (value, meta, { resetForm }) => {
            const {
              contactEventType,
              contactEventSubtype,
              title,
              description,
              legalEntity,
              premises
            } = value;

            const event: ContactEvent = {
              title,
              description,
              legalEntityId: legalEntity
                ? legalEntity.cmAcmCaseIdentifier
                : undefined,
              premisesId: premises ? premises.cmAcmCaseIdentifier : undefined,
              type: contactEventType.value,
              subType: contactEventSubtype,
              tempCaseId
            };

            await props.submitEvent(event, () => {
              resetForm();
              resetTempCaseId(uuid());
              showMessage("Your response was successfully submitted", "success");
            });
          }}
        >
          <OnUnSaveChange />
          <FieldValueOnChange>
            {({ action, formState, updateFormValues }) => {
              if (
                action.name === "legalEntity" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const nextFormValues = {
                  ...formState.values,
                  premises: null
                };
                updateFormValues(nextFormValues);
              } else if (
                action.name === "premises" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const formLegalEntity = formState.values.legalEntity;
                if (formLegalEntity) return null;
                const propsLegalEntity = props.premises.find(
                  p =>
                    p.cmAcmCaseIdentifier === action.payload.cmAcmCaseIdentifier
                );
                const nextFormValues = {
                  ...formState.values,
                  legalEntity: (propsLegalEntity as Premises).parentLegalEntity
                };
                updateFormValues(nextFormValues);
              } else if (
                action.name === "contactEventType" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const nextFormValues = {
                  ...formState.values,
                  contactEventSubtype: ""
                };
                updateFormValues(nextFormValues);
              }

              return null;
            }}
          </FieldValueOnChange>

              <FormValues>
                {({ formValues }) => {
                  const { legalEntity, contactEventType } = formValues;

                  const legalEntitiesSelectList = props.premises.reduce(
                    (result: Suggestion[], premise) => {
                      const le = premise.parentLegalEntity;
                      if (!le || !le.viewable) return result;
                      if (
                        result.some(
                          r =>
                            r.value.cmAcmCaseIdentifier === le.cmAcmCaseIdentifier
                        )
                      )
                        return result;
                      result.push({
                        label: le.legalEntityName,
                        value: le
                      });
                      return result;
                    },
                    []
                  );

                  const premisesSelectList = props.premises
                    .filter(premise => {
                      if (!premise.parentLegalEntity) return false;
                      if (!legalEntity) return true;
                      return (
                        premise.parentLegalEntity.cmAcmCaseIdentifier ===
                          (legalEntity as LegalEntity).cmAcmCaseIdentifier
                      );
                    })
                    .map(premise => ({
                      label: premise.premisesName,
                      value: premise
                    }));

                  const subtypeList = contactEventType
                    ? _.flatten(
                        props.contactEventTypes
                          .filter(t => t.value === contactEventType.value)
                          .map(t => t.subType)
                      )
                    : [];
                  return (
                    <section className={classes.selectWrapper}>
                      <div className={classes.row}>
                        <SelectField
                          id="legal-entity"
                          label="Legal Entity"
                          name="legalEntity"
                          getSelectedValue={selected =>
                            selected ? selected.value : null
                          }
                          className={classes.select}
                          placeholder="Select legal entity"
                          getValueDisplay={value =>
                            value ? value.legalEntityName : ""
                          }
                          suggestions={legalEntitiesSelectList}
                        />
                        <SelectField
                          id="premises"
                          label="Premises"
                          name="premises"
                          getSelectedValue={selected =>
                            selected ? selected.value : null
                          }
                          className={classes.select}
                          placeholder="Select premises"
                          getValueDisplay={value =>
                            value ? value.premisesName : ""
                          }
                          suggestions={premisesSelectList}
                        />
                      </div>
                      <div className={classes.row}>
                        <SelectField
                          required
                          id="contact-event-type"
                          label="Contact event type *"
                          name="contactEventType"
                          className={classes.select}
                          placeholder="Select type"
                          getValueDisplay={value => (value ? value.label : "")}
                          suggestions={props.contactEventTypes.map(
                            (type: any) => ({
                              label: type.display,
                              value: type.value
                            })
                          )}
                        />

                        <SelectField
                          required
                          isDisabled={!contactEventType}
                          className={classes.select}
                          id="contact-event-subtype"
                          label="Contact event subtype *"
                          name="contactEventSubtype"
                          getSelectedValue={selected =>
                            selected ? selected.value : null
                          }
                          placeholder="Select subtype"
                          suggestions={subtypeList.map((subtype: string) => ({
                            label: subtype,
                            value: subtype
                          }))}
                          getValueDisplay={value => (value ? value : "")}
                        />
                      </div>
                    </section>
                  );
                }}
              </FormValues>

              <FormTextField
                className={classes.titleField}
                required
                characterCounter
                inputProps={{ maxLength: 200 }}
                id="title"
                name="title"
                label="Title"
              />
              <FormTextField
                className={classes.descriptionField}
                required
                characterCounter
                inputProps={{ maxLength: 1300 }}
                id="description"
                name="description"
                label="Details"
              />

              <section className={classes.dropzoneWrapper}>
                <Typography color="textSecondary" variant="caption">
                  Attach any supporting documents
                </Typography>
                <FileUploadField
                  handleError={(error: Error) => {
                    showMessage(
                      _.get(error, "response.data.message") || error.message,
                      "error"
                    );
                  }}
                  onFileUpload={(files: File[]) =>
                    Promise.all(
                      files.map(file => {
                        const formData: any = new FormData();
                        formData.append("uploadfile", file);
                        return api.post(
                          `/event/uploadFile/${tempCaseId}`,
                          formData
                        );
                      })
                    )
                  }
                  onFileRemove={(file: File) =>
                    api.put(`/event/removeFile/${tempCaseId}`, file.name, {
                      headers: {
                        "Content-Type": "application/json"
                      }
                    })
                  }
                  acceptedFileTypes={GENERAL_FILE_TYPES}
                  className={classes.dropzone}
                  hint={GENERAL_FILE_HINT_25_FILE_LIMIT}
                  name="attachments"
                  id="attachments"
                  maxFileCount={25}
                  maxTotalByteSize={MAX_FILE_SIZE}
                />
              </section>
              <div className={classes.buttonWrapper}>
                <SubmitButton label="Submit" />
                <ResetButton
                  label="Reset"
                  onReset={() => {
                    api.put(`/event/removeFiles/${tempCaseId}`);
                  }}
                />
                <Button
                  onClick={() => {
                    props.history.push(`${routes.HOME.url}`);
                  }}
                  id="close"
                >
                  Close
                </Button>
              </div>

        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapStateToProps = ({ contactEvent, premises }: any) => ({
  contactEventTypes: contactEvent.contactEventTypes,
  premises: premises.premises
});

const mapDispatchToProps = {
  submitEvent: contactEventActions.submitEvent,
  getPremises: premisesActions.getPremises
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RaiseContactEvent);
